/**
 * Security Incident Response Service
 * Provides comprehensive incident response capabilities
 * Phase 5E: Security Monitoring & Incident Response
 */

import Logger from './logger';
import automatedSecurityResponse from './automatedSecurityResponse';
import centralizedAuditLogService from './centralizedAuditLogService';

export interface SecurityIncident {
  id: string;
  type: 'data_breach' | 'unauthorized_access' | 'malware_detection' | 'ddos_attack' | 'phishing_attempt' | 'insider_threat' | 'system_compromise' | 'policy_violation';
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'detected' | 'investigating' | 'contained' | 'resolved' | 'closed';
  title: string;
  description: string;
  source: string;
  affectedSystems: string[];
  affectedUsers: string[];
  detectedAt: Date;
  lastUpdated: Date;
  assignedTo?: string;
  priority: number; // 1-10, 10 being highest
  tags: string[];
  evidence: SecurityEvidence[];
  timeline: IncidentTimelineEvent[];
  resolution?: IncidentResolution;
  lessonsLearned?: string[];
  preventionMeasures?: string[];
}

export interface SecurityEvidence {
  id: string;
  type: 'log' | 'screenshot' | 'file' | 'network_capture' | 'user_report' | 'system_alert';
  description: string;
  data: any;
  collectedAt: Date;
  collectedBy: string;
  integrityHash?: string;
  location: string;
}

export interface IncidentTimelineEvent {
  id: string;
  timestamp: Date;
  action: string;
  description: string;
  performedBy: string;
  category: 'detection' | 'investigation' | 'containment' | 'recovery' | 'resolution' | 'communication';
  metadata?: Record<string, any>;
}

export interface IncidentResolution {
  id: string;
  resolvedAt: Date;
  resolvedBy: string;
  resolutionType: 'contained' | 'patched' | 'isolated' | 'recovered' | 'mitigated';
  description: string;
  actionsTaken: string[];
  followUpRequired: boolean;
  followUpDate?: Date;
  cost: number;
  downtime: number; // in minutes
}

export interface IncidentResponseConfig {
  enableAutoResponse: boolean;
  enableEscalation: boolean;
  enableNotification: boolean;
  enableForensics: boolean;
  responseTimeThresholds: {
    low: number; // minutes
    medium: number;
    high: number;
    critical: number;
  };
  escalationThresholds: {
    low: number; // minutes
    medium: number;
    high: number;
    critical: number;
  };
  notificationChannels: string[];
  forensicsRetentionDays: number;
  autoResponseRules: AutoResponseRule[];
}

export interface AutoResponseRule {
  id: string;
  name: string;
  conditions: {
    incidentType?: string[];
    severity?: string[];
    source?: string[];
    affectedSystems?: string[];
  };
  actions: {
    autoAssign?: string;
    autoEscalate?: boolean;
    autoContain?: boolean;
    sendNotification?: boolean;
    createTicket?: boolean;
    runScript?: string;
  };
  enabled: boolean;
  priority: number;
}

export interface IncidentResponseStats {
  totalIncidents: number;
  incidentsByType: Record<string, number>;
  incidentsBySeverity: Record<string, number>;
  incidentsByStatus: Record<string, number>;
  averageResolutionTime: number;
  averageResponseTime: number;
  escalationRate: number;
  containmentRate: number;
  falsePositiveRate: number;
  recentIncidents: SecurityIncident[];
  topAffectedSystems: Array<{ system: string; count: number }>;
  responseTeamPerformance: Array<{ member: string; incidentsResolved: number; averageTime: number }>;
}

class SecurityIncidentResponseService {
  private static instance: SecurityIncidentResponseService;
  private incidents: Map<string, SecurityIncident> = new Map();
  private config: Required<IncidentResponseConfig>;
  private stats: IncidentResponseStats;
  private responseTeam: Map<string, { name: string; role: string; availability: boolean; skills: string[] }> = new Map();
  private notificationListeners: Map<string, (incident: SecurityIncident) => void> = new Map();

  constructor(config: Partial<IncidentResponseConfig> = {}) {
    this.config = {
      enableAutoResponse: config.enableAutoResponse ?? true,
      enableEscalation: config.enableEscalation ?? true,
      enableNotification: config.enableNotification ?? true,
      enableForensics: config.enableForensics ?? true,
      responseTimeThresholds: {
        low: config.responseTimeThresholds?.low ?? 60,
        medium: config.responseTimeThresholds?.medium ?? 30,
        high: config.responseTimeThresholds?.high ?? 15,
        critical: config.responseTimeThresholds?.critical ?? 5
      },
      escalationThresholds: {
        low: config.escalationThresholds?.low ?? 120,
        medium: config.escalationThresholds?.medium ?? 60,
        high: config.escalationThresholds?.high ?? 30,
        critical: config.escalationThresholds?.critical ?? 10
      },
      notificationChannels: config.notificationChannels ?? ['email', 'dashboard', 'slack'],
      forensicsRetentionDays: config.forensicsRetentionDays ?? 90,
      autoResponseRules: config.autoResponseRules ?? []
    };

    this.stats = {
      totalIncidents: 0,
      incidentsByType: {},
      incidentsBySeverity: {},
      incidentsByStatus: {},
      averageResolutionTime: 0,
      averageResponseTime: 0,
      escalationRate: 0,
      containmentRate: 0,
      falsePositiveRate: 0,
      recentIncidents: [],
      topAffectedSystems: [],
      responseTeamPerformance: []
    };

    this.initializeService();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): SecurityIncidentResponseService {
    if (!SecurityIncidentResponseService.instance) {
      SecurityIncidentResponseService.instance = new SecurityIncidentResponseService();
    }
    return SecurityIncidentResponseService.instance;
  }

  /**
   * Initialize the service
   */
  private initializeService(): void {
    try {
      // Set up response team
      this.setupResponseTeam();
      
      // Set up auto-response monitoring
      if (this.config.enableAutoResponse) {
        this.setupAutoResponse();
      }

      // Set up escalation monitoring
      if (this.config.enableEscalation) {
        this.setupEscalationMonitoring();
      }

      // Set up forensics cleanup
      if (this.config.enableForensics) {
        this.setupForensicsCleanup();
      }

      Logger.info('Security Incident Response Service initialized', {
        config: this.config,
        teamSize: this.responseTeam.size
      });

    } catch (error) {
      Logger.error('Failed to initialize Security Incident Response Service', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Set up response team
   */
  private setupResponseTeam(): void {
    // Default response team members
    this.responseTeam.set('admin', {
      name: 'System Administrator',
      role: 'Incident Commander',
      availability: true,
      skills: ['incident_response', 'forensics', 'system_administration']
    });

    this.responseTeam.set('security', {
      name: 'Security Analyst',
      role: 'Security Specialist',
      availability: true,
      skills: ['threat_analysis', 'malware_analysis', 'network_security']
    });

    this.responseTeam.set('developer', {
      name: 'Security Developer',
      role: 'Technical Specialist',
      availability: true,
      skills: ['code_analysis', 'vulnerability_assessment', 'system_architecture']
    });
  }

  /**
   * Set up auto-response system
   */
  private setupAutoResponse(): void {
    // Monitor for new incidents
    setInterval(() => {
      this.processAutoResponse();
    }, 30000); // Check every 30 seconds

    Logger.info('Auto-response system enabled');
  }

  /**
   * Set up escalation monitoring
   */
  private setupEscalationMonitoring(): void {
    // Monitor for escalation needs
    setInterval(() => {
      this.checkEscalationNeeds();
    }, 60000); // Check every minute

    Logger.info('Escalation monitoring enabled');
  }

  /**
   * Set up forensics cleanup
   */
  private setupForensicsCleanup(): void {
    // Clean up old forensics data
    setInterval(() => {
      this.cleanupForensicsData();
    }, 24 * 60 * 60 * 1000); // Daily cleanup

    Logger.info('Forensics cleanup enabled');
  }

  /**
   * Create a new security incident
   */
  public createIncident(
    type: SecurityIncident['type'],
    severity: SecurityIncident['severity'],
    title: string,
    description: string,
    source: string,
    affectedSystems: string[] = [],
    affectedUsers: string[] = [],
    evidence: SecurityEvidence[] = []
  ): SecurityIncident {
    const incident: SecurityIncident = {
      id: this.generateIncidentId(),
      type,
      severity,
      status: 'detected',
      title,
      description,
      source,
      affectedSystems,
      affectedUsers,
      detectedAt: new Date(),
      lastUpdated: new Date(),
      priority: this.calculatePriority(severity, type),
      tags: this.generateTags(type, severity),
      evidence,
      timeline: [{
        id: this.generateTimelineEventId(),
        timestamp: new Date(),
        action: 'incident_created',
        description: 'Security incident detected and created',
        performedBy: 'system',
        category: 'detection'
      }]
    };

    try {
      // Store incident
      this.incidents.set(incident.id, incident);
      
      // Update statistics
      this.updateStatistics(incident);
      
      // Process auto-response
      if (this.config.enableAutoResponse) {
        this.processIncidentAutoResponse(incident);
      }

      // Trigger automated security response workflows
      try {
        automatedSecurityResponse.processIncident(incident);
      } catch (error) {
        Logger.error('Failed to trigger automated security response workflow', error instanceof Error ? error : new Error('Unknown error'));
      }

      // Send notifications
      if (this.config.enableNotification) {
        this.sendIncidentNotifications(incident);
      }

      // Log incident creation
      Logger.warn('Security incident created', {
        incidentId: incident.id,
        type: incident.type,
        severity: incident.severity,
        title: incident.title
      });

      // Log to centralized audit log
      centralizedAuditLogService.logEntry({
        userId: incident.affectedUsers[0],
        action: 'security_incident_created',
        resource: 'security_incident',
        resourceId: incident.id,
        operation: 'create',
        result: 'success',
        severity: incident.severity,
        category: 'security',
        details: {
          type: incident.type,
          title: incident.title,
          source: incident.source,
          affectedSystems: incident.affectedSystems,
          affectedUsers: incident.affectedUsers
        }
      }).catch(error => {
        Logger.error('Failed to log security incident to audit log', error instanceof Error ? error : new Error('Unknown error'));
      });

      return incident;

    } catch (error) {
      Logger.error('Failed to create security incident', error instanceof Error ? error : new Error('Unknown error'));
      throw error;
    }
  }

  /**
   * Update incident status
   */
  public updateIncidentStatus(
    incidentId: string,
    status: SecurityIncident['status'],
    updatedBy: string,
    description?: string,
    evidence?: SecurityEvidence[]
  ): boolean {
    const incident = this.incidents.get(incidentId);
    if (!incident) {
      Logger.warn('Incident not found for status update', { incidentId });
      return false;
    }

    try {
      const oldStatus = incident.status;
      incident.status = status;
      incident.lastUpdated = new Date();

      // Add evidence if provided
      if (evidence) {
        incident.evidence.push(...evidence);
      }

      // Add timeline event
      incident.timeline.push({
        id: this.generateTimelineEventId(),
        timestamp: new Date(),
        action: 'status_updated',
        description: description || `Status changed from ${oldStatus} to ${status}`,
        performedBy: updatedBy,
        category: this.getTimelineCategory(status),
        metadata: { oldStatus, newStatus: status }
      });

      // Update statistics
      this.updateStatistics(incident);

      // Send notifications for status changes
      if (this.config.enableNotification && status !== oldStatus) {
        this.sendStatusChangeNotification(incident, oldStatus, status);
      }

      Logger.info('Incident status updated', {
        incidentId,
        oldStatus,
        newStatus: status,
        updatedBy
      });

      return true;

    } catch (error) {
      Logger.error('Failed to update incident status', error instanceof Error ? error : new Error('Unknown error'));
      return false;
    }
  }

  /**
   * Assign incident to team member
   */
  public assignIncident(incidentId: string, assigneeId: string, assignedBy: string): boolean {
    const incident = this.incidents.get(incidentId);
    if (!incident) {
      Logger.warn('Incident not found for assignment', { incidentId });
      return false;
    }

    const assignee = this.responseTeam.get(assigneeId);
    if (!assignee) {
      Logger.warn('Assignee not found in response team', { assigneeId });
      return false;
    }

    try {
      incident.assignedTo = assigneeId;
      incident.lastUpdated = new Date();

      // Add timeline event
      incident.timeline.push({
        id: this.generateTimelineEventId(),
        timestamp: new Date(),
        action: 'incident_assigned',
        description: `Incident assigned to ${assignee.name}`,
        performedBy: assignedBy,
        category: 'investigation',
        metadata: { assigneeId, assigneeName: assignee.name }
      });

      Logger.info('Incident assigned', {
        incidentId,
        assigneeId,
        assigneeName: assignee.name,
        assignedBy
      });

      return true;

    } catch (error) {
      Logger.error('Failed to assign incident', error instanceof Error ? error : new Error('Unknown error'));
      return false;
    }
  }

  /**
   * Resolve incident
   */
  public resolveIncident(
    incidentId: string,
    resolvedBy: string,
    resolution: Omit<IncidentResolution, 'id' | 'resolvedAt' | 'resolvedBy'>
  ): boolean {
    const incident = this.incidents.get(incidentId);
    if (!incident) {
      Logger.warn('Incident not found for resolution', { incidentId });
      return false;
    }

    try {
      // Create resolution
      const incidentResolution: IncidentResolution = {
        id: this.generateResolutionId(),
        resolvedAt: new Date(),
        resolvedBy,
        ...resolution
      };

      incident.resolution = incidentResolution;
      incident.status = 'resolved';
      incident.lastUpdated = new Date();

      // Add timeline event
      incident.timeline.push({
        id: this.generateTimelineEventId(),
        timestamp: new Date(),
        action: 'incident_resolved',
        description: `Incident resolved: ${resolution.description}`,
        performedBy: resolvedBy,
        category: 'resolution',
        metadata: { resolutionType: resolution.resolutionType, cost: resolution.cost }
      });

      // Update statistics
      this.updateStatistics(incident);

      // Send resolution notification
      if (this.config.enableNotification) {
        this.sendResolutionNotification(incident);
      }

      Logger.info('Incident resolved', {
        incidentId,
        resolvedBy,
        resolutionType: resolution.resolutionType,
        cost: resolution.cost,
        downtime: resolution.downtime
      });

      return true;

    } catch (error) {
      Logger.error('Failed to resolve incident', error instanceof Error ? error : new Error('Unknown error'));
      return false;
    }
  }

  /**
   * Get incident by ID
   */
  public getIncident(incidentId: string): SecurityIncident | null {
    return this.incidents.get(incidentId) || null;
  }

  /**
   * Get all incidents
   */
  public getAllIncidents(): SecurityIncident[] {
    return Array.from(this.incidents.values());
  }

  /**
   * Get incidents by status
   */
  public getIncidentsByStatus(status: SecurityIncident['status']): SecurityIncident[] {
    return this.getAllIncidents().filter(incident => incident.status === status);
  }

  /**
   * Get incidents by severity
   */
  public getIncidentsBySeverity(severity: SecurityIncident['severity']): SecurityIncident[] {
    return this.getAllIncidents().filter(incident => incident.severity === severity);
  }

  /**
   * Get response statistics
   */
  public getResponseStats(): IncidentResponseStats {
    return { ...this.stats };
  }

  /**
   * Process auto-response for incident
   */
  private processIncidentAutoResponse(incident: SecurityIncident): void {
    try {
      for (const rule of this.config.autoResponseRules) {
        if (!rule.enabled) continue;

        if (this.matchesRule(incident, rule)) {
          this.executeRule(incident, rule);
        }
      }
    } catch (error) {
      Logger.error('Auto-response processing failed', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Check if incident matches rule
   */
  private matchesRule(incident: SecurityIncident, rule: AutoResponseRule): boolean {
    const conditions = rule.conditions;

    if (conditions.incidentType && !conditions.incidentType.includes(incident.type)) {
      return false;
    }

    if (conditions.severity && !conditions.severity.includes(incident.severity)) {
      return false;
    }

    if (conditions.source && !conditions.source.includes(incident.source)) {
      return false;
    }

    if (conditions.affectedSystems && !incident.affectedSystems.some(system => 
      conditions.affectedSystems!.includes(system))) {
      return false;
    }

    return true;
  }

  /**
   * Execute auto-response rule
   */
  private executeRule(incident: SecurityIncident, rule: AutoResponseRule): void {
    try {
      const actions = rule.actions;

      if (actions.autoAssign) {
        this.assignIncident(incident.id, actions.autoAssign, 'auto-response');
      }

      if (actions.autoEscalate) {
        this.escalateIncident(incident.id, 'auto-escalation');
      }

      if (actions.autoContain) {
        this.containIncident(incident.id, 'auto-containment');
      }

      if (actions.sendNotification) {
        this.sendIncidentNotifications(incident);
      }

      Logger.info('Auto-response rule executed', {
        incidentId: incident.id,
        ruleId: rule.id,
        ruleName: rule.name
      });

    } catch (error) {
      Logger.error('Failed to execute auto-response rule', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Escalate incident
   */
  private escalateIncident(incidentId: string, escalatedBy: string): void {
    const incident = this.incidents.get(incidentId);
    if (!incident) return;

    // Add escalation timeline event
    incident.timeline.push({
      id: this.generateTimelineEventId(),
      timestamp: new Date(),
      action: 'incident_escalated',
      description: 'Incident escalated due to auto-response rule',
      performedBy: escalatedBy,
      category: 'investigation'
    });

    Logger.info('Incident escalated', { incidentId, escalatedBy });
  }

  /**
   * Contain incident
   */
  private containIncident(incidentId: string, containedBy: string): void {
    const incident = this.incidents.get(incidentId);
    if (!incident) return;

    // Add containment timeline event
    incident.timeline.push({
      id: this.generateTimelineEventId(),
      timestamp: new Date(),
      action: 'incident_contained',
      description: 'Incident contained due to auto-response rule',
      performedBy: containedBy,
      category: 'containment'
    });

    Logger.info('Incident contained', { incidentId, containedBy });
  }

  /**
   * Send incident notifications
   */
  private sendIncidentNotifications(incident: SecurityIncident): void {
    try {
      for (const [listenerId, listener] of this.notificationListeners) {
        try {
          listener(incident);
        } catch (error) {
          Logger.error('Notification listener failed', error instanceof Error ? error : new Error('Unknown error'));
        }
      }
    } catch (error) {
      Logger.error('Failed to send incident notifications', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Send status change notification
   */
  private sendStatusChangeNotification(
    incident: SecurityIncident,
    oldStatus: SecurityIncident['status'],
    newStatus: SecurityIncident['status']
  ): void {
    // Implementation for status change notifications
    Logger.info('Status change notification sent', {
      incidentId: incident.id,
      oldStatus,
      newStatus
    });
  }

  /**
   * Send resolution notification
   */
  private sendResolutionNotification(incident: SecurityIncident): void {
    // Implementation for resolution notifications
    Logger.info('Resolution notification sent', {
      incidentId: incident.id,
      resolutionType: incident.resolution?.resolutionType
    });
  }

  /**
   * Update statistics
   */
  private updateStatistics(incident: SecurityIncident): void {
    this.stats.totalIncidents = this.incidents.size;
    
    // Update type statistics
    this.stats.incidentsByType[incident.type] = (this.stats.incidentsByType[incident.type] || 0) + 1;
    
    // Update severity statistics
    this.stats.incidentsBySeverity[incident.severity] = (this.stats.incidentsBySeverity[incident.severity] || 0) + 1;
    
    // Update status statistics
    this.stats.incidentsByStatus[incident.status] = (this.stats.incidentsByStatus[incident.status] || 0) + 1;

    // Update recent incidents
    this.stats.recentIncidents = this.getAllIncidents()
      .sort((a, b) => b.detectedAt.getTime() - a.detectedAt.getTime())
      .slice(0, 10);

    // Update affected systems
    this.updateAffectedSystemsStats();
  }

  /**
   * Update affected systems statistics
   */
  private updateAffectedSystemsStats(): void {
    const systemCounts: Record<string, number> = {};
    
    for (const incident of this.incidents.values()) {
      for (const system of incident.affectedSystems) {
        systemCounts[system] = (systemCounts[system] || 0) + 1;
      }
    }

    this.stats.topAffectedSystems = Object.entries(systemCounts)
      .map(([system, count]) => ({ system, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }

  /**
   * Process auto-response
   */
  private processAutoResponse(): void {
    // Implementation for periodic auto-response processing
  }

  /**
   * Check escalation needs
   */
  private checkEscalationNeeds(): void {
    const now = new Date();
    
    for (const incident of this.incidents.values()) {
      if (incident.status === 'resolved' || incident.status === 'closed') continue;

      const timeSinceDetection = now.getTime() - incident.detectedAt.getTime();
      const threshold = this.config.escalationThresholds[incident.severity] * 60 * 1000;

      if (timeSinceDetection > threshold) {
        this.escalateIncident(incident.id, 'escalation-monitor');
      }
    }
  }

  /**
   * Cleanup forensics data
   */
  private cleanupForensicsData(): void {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - this.config.forensicsRetentionDays);

    for (const incident of this.incidents.values()) {
      if (incident.detectedAt < cutoffDate && incident.status === 'closed') {
        // Clean up old forensics data
        incident.evidence = incident.evidence.filter(evidence => 
          evidence.collectedAt > cutoffDate
        );
      }
    }

    Logger.info('Forensics data cleanup completed');
  }

  /**
   * Calculate incident priority
   */
  private calculatePriority(severity: SecurityIncident['severity'], type: SecurityIncident['type']): number {
    const severityWeights = { low: 2, medium: 4, high: 7, critical: 10 };
    const typeWeights: Record<SecurityIncident['type'], number> = {
      data_breach: 10,
      system_compromise: 9,
      unauthorized_access: 8,
      malware_detection: 7,
      ddos_attack: 6,
      insider_threat: 8,
      phishing_attempt: 5,
      policy_violation: 3
    };

    return Math.min(10, Math.max(1, 
      severityWeights[severity] + (typeWeights[type] || 5)
    ));
  }

  /**
   * Generate incident tags
   */
  private generateTags(type: SecurityIncident['type'], severity: SecurityIncident['severity']): string[] {
    const tags: string[] = [type, severity];
    
    // Add additional tags based on type
    switch (type) {
      case 'data_breach':
        tags.push('pii', 'gdpr', 'compliance');
        break;
      case 'malware_detection':
        tags.push('malware', 'virus', 'threat');
        break;
      case 'ddos_attack':
        tags.push('ddos', 'network', 'availability');
        break;
      case 'phishing_attempt':
        tags.push('phishing', 'social-engineering', 'user-education');
        break;
    }

    return tags;
  }

  /**
   * Get timeline category for status
   */
  private getTimelineCategory(status: SecurityIncident['status']): IncidentTimelineEvent['category'] {
    switch (status) {
      case 'detected': return 'detection';
      case 'investigating': return 'investigation';
      case 'contained': return 'containment';
      case 'resolved': return 'resolution';
      case 'closed': return 'resolution';
      default: return 'investigation';
    }
  }

  /**
   * Generate incident ID
   */
  private generateIncidentId(): string {
    return `INC-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
  }

  /**
   * Generate timeline event ID
   */
  private generateTimelineEventId(): string {
    return `EVT-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
  }

  /**
   * Generate resolution ID
   */
  private generateResolutionId(): string {
    return `RES-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
  }

  /**
   * Add notification listener
   */
  public addNotificationListener(id: string, listener: (incident: SecurityIncident) => void): void {
    this.notificationListeners.set(id, listener);
  }

  /**
   * Remove notification listener
   */
  public removeNotificationListener(id: string): void {
    this.notificationListeners.delete(id);
  }
}

// Export singleton instance
const securityIncidentResponse = new SecurityIncidentResponseService();
export default securityIncidentResponse;
